"use client";

import React, { useRef, useState, useCallback } from 'react';
import styles from './_gc-step-gc-therapy-adjustments.module.scss';
import GcStepShell from '../../components/gc-step-shell';
import GcHceDisplay from '../../components/gc-hce-display';
import { FormInputCheckbox, FormInputCalendar, FormInputText } from '@/components/form';
import Button from '@/components/button';
import {
  GC_TYPES,
  GC_CALC_POPULATION,
  GC_CALC_DOWN_TITRATION_EXPERIENCE,
  computeTotalHce,
  HCE_THRESHOLDS,
} from '@/data/gc-calculator-data';
import { validateDateWithinDayWindow } from '../../utils';

const HYDROCORTISONE_ID = 'hydrocortisone';

// Shared position fn — 8px above trigger, horizontally centered in the 628px calc column
function gcTooltipPositionFn(target, tooltipEl) {
  const col = target.closest('[data-gc-calc-col]');
  const targetRect = target.getBoundingClientRect();
  const tipRect    = tooltipEl.getBoundingClientRect();
  const colRect    = col ? col.getBoundingClientRect() : targetRect;
  return {
    top:  targetRect.top + window.scrollY - 8 - tipRect.height,
    left: colRect.left + window.scrollX + (colRect.width - tipRect.width) / 2,
  };
}

// Tooltip data — shared between the innerStep 0 and innerStep >= 1 switch callouts
const SWITCHING_GC_TOOLTIP = {
  openClass:  'gc-calc-tooltip',
  maxWidth:   '384px',
  positionFn: gcTooltipPositionFn,
  content: (
    <>
      <p className="!mb-4">
        Longer-acting GCs (eg, dexamethasone, prednisone) are associated with
        greater risk of comorbidities than hydrocortisone.
      </p>
      <p>
        Switching to a shorter-acting GC before starting CRENESSITY may help manage GC
        withdrawal symptoms. Switching after starting CRENESSITY may help maintain androgen control
        while also allowing you to coordinate GC dosing with twice-daily
        CRENESSITY (if applicable).
      </p>
    </>
  ),
};

// Tooltip data — "When initiating CRENESSITY…" guidance note
const GC_REDUCTION_TOOLTIP = {
  openClass:  'gc-calc-tooltip',
  maxWidth:   '384px',
  positionFn: gcTooltipPositionFn,
  content: (
    <>
      <p className="!mb-2">Common symptoms of GC withdrawal include:</p>
      <ul className="!mb-3">
        <li>General malaise</li>
        <li>Fatigue</li>
        <li>Nausea</li>
        <li>Arthralgia</li>
        <li>Sleep disturbances</li>
        <li>Mood changes</li>
      </ul>
      <p>
        The risk of GC withdrawal symptoms is greater with higher-potency GCs
        (eg, dexamethasone).
      </p>
    </>
  ),
};

// Inner screen index — 0: GC type selection, 1: HCe note (non-HC only), 2 (non-HC) / 1 (HC): full guidance

function isValid(types, doses) {
  if (types.length === 0) return false;
  if (types.some((id) => !(parseFloat(doses[id]) > 0))) return false;
  return true;
}

function guidanceBranch(hceTotal) {
  if (hceTotal >= HCE_THRESHOLDS.HIGH)    return 'high';
  if (hceTotal >= HCE_THRESHOLDS.MID_LOW) return 'mid';
  return 'low';
}

/**
 * @description GC OPTIMIZE™ Calculator — Step 3: GC Therapy & Adjustments
 *
 * The user selects ALL concurrent GC types (multi-select checkboxes).
 * A dose input (mg/day adult; mg/m²/day pediatric) appears for each selected type.
 *
 * When hydrocortisone is NOT among the selected types:
 *   • HCe conversion note is displayed
 *   • Computed "Based on those equivalents…" sentence is shown (once any dose > 0)
 *   • "Is switching to a shorter-acting GC a goal…?" YES/NO question appears
 *
 * Saved fields (via dispatch):
 *   gcSelectedTypes         — string[]
 *   gcDoses                 — { [typeId]: string }
 *   switchToShorterActingGc — 'yes' | 'no' | ''
 * Plus: SET_HCE_TOTAL dispatched with the computed numeric value.
 */
function GcStepGcTherapyAdjustments({ state, dispatch, onBack, onNext, isReview, stepNumber }) {
  const savedTypes = state.formValues.gcSelectedTypes ?? [];
  const savedDoses = state.formValues.gcDoses         ?? {};
  const wasPreviouslySaved = Array.isArray(savedTypes) && savedTypes.length > 0;

  // ── Rendered state ───────────────────────────────────────────────────────────────
  const [selectedTypes,  setSelectedTypes]  = useState(savedTypes);
  const [computedHce,    setComputedHce]    = useState(() => computeTotalHce(savedDoses, state.population));
  const [innerStep,      setInnerStep]      = useState(0);
  const [isNextDisabled, setIsNextDisabled] = useState(() => !isValid(savedTypes, savedDoses));
  const [isClinDateFilled, setIsClinDateFilled] = useState(() => Boolean(state.formValues.clinicalAssessmentDate));

  // ── Refs — no re-render on change ────────────────────────────────────────
  const dosesRef = useRef({ ...savedDoses });
  const hceRef   = useRef(computeTotalHce(savedDoses, state.population));

  // ── Derived ───────────────────────────────────────────────────────────────
  const hasHydrocortisone = selectedTypes.includes(HYDROCORTISONE_ID);
  const showSwitchCallout = selectedTypes.some((id) => id !== HYDROCORTISONE_ID);
  const showHceSection    = showSwitchCallout; // HCe note only relevant for non-HC types
  const doseUnit          = state.population === GC_CALC_POPULATION.PEDIATRIC
    ? 'mg/m\u00B2/day' : 'mg/day';
  // Adult path uses: 0=current GC therapy, 1=guidance + experience, 2=schedule.
  // Pediatric path uses: 0=current GC therapy, 1=guidance, 2=schedule.
  const maxInnerStep      = state.population === GC_CALC_POPULATION.ADULT
    ? 2
    : 2;
  const isFinalInnerStep  = innerStep >= maxInnerStep;
  const effectiveNextDisabled = isNextDisabled ||
    (isFinalInnerStep && state.population === GC_CALC_POPULATION.ADULT && !isClinDateFilled);
  const branch            = guidanceBranch(computedHce);
  const showDownTitrationExperience = state.population === GC_CALC_POPULATION.ADULT
    && selectedTypes.length > 0
    && branch !== 'high';
  const isPediatric = state.population === GC_CALC_POPULATION.PEDIATRIC;

  // ── Refs for GC reduction schedule fields ─────────────────────────────────
  const savedGoals    = state.formValues.gcReductionGoal ?? {};
  const savedExperience = state.formValues.gcDownTitrationExperience ?? '';
  const gcGoalRef     = useRef(typeof savedGoals === 'object' && savedGoals !== null ? { ...savedGoals } : {});
  const [downTitrationExperience, setDownTitrationExperience] = useState(savedExperience);
  const labDateRef    = useRef(state.formValues.labDrawDate ?? '');
  const clinDateRef   = useRef(state.formValues.clinicalAssessmentDate ?? '');
  const labCalRef     = useRef(null);
  const [goalErrors, setGoalErrors] = useState({});

  // ── Recompute HCe and re-evaluate validity whenever types/doses change ────
  const refresh = useCallback((types) => {
    const doseMap = Object.fromEntries(types.map((id) => [id, dosesRef.current[id] ?? '']));
    const hce = computeTotalHce(doseMap, state.population);
    hceRef.current = hce;
    setComputedHce(hce);
    setIsNextDisabled(!isValid(types, dosesRef.current));

    // If this step was previously saved and dose edits changed whether the
    // 25-50 HCe experience question applies, clear stale saved experience.
    if (wasPreviouslySaved && state.population === GC_CALC_POPULATION.ADULT) {
      const wasEligible = guidanceBranch(state.hceTotal) === 'mid';
      const isEligible = guidanceBranch(hce) === 'mid';
      if (wasEligible !== isEligible && downTitrationExperience) {
        setDownTitrationExperience('');
      }
    }
  }, [downTitrationExperience, state.hceTotal, state.population, wasPreviouslySaved]);

  // ── Type checkbox toggle (multi-select) ─────────────────────────────────────
  const handleTypeChange = useCallback((id) => {
    setSelectedTypes((prev) => {
      const next = prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id];
      refresh(next);
      if (next.length === 0) setInnerStep(0);
      return next;
    });
  }, [refresh]);

  // Re-run goal error validation against updated doses (called when current dose changes).
  const revalidateGoalErrors = useCallback((types) => {
    const newBranch = guidanceBranch(hceRef.current);
    const newErrors = {};
    types.forEach((typeId) => {
      const gcType = GC_TYPES.find((t) => t.id === typeId);
      if (!gcType) return;
      const currentRaw = parseFloat(dosesRef.current[typeId]) || 0;
      const targetGoalRaw = parseFloat(gcGoalRef.current[typeId]) || 0;
      const reductionData  = gcType.recommendedReduction?.[state.population];
      const reductionRange = reductionData
        ? (newBranch === 'high' ? reductionData.aboveHigh : reductionData.belowOrEqualHigh)
        : null;
      if (targetGoalRaw > 0 && currentRaw > 0) {
        let isError;
        if (reductionData?.unitType === 'mg' && reductionRange) {
          const reductionAmountRaw = currentRaw - targetGoalRaw;
          isError = reductionAmountRaw < reductionRange.min || reductionAmountRaw > reductionRange.max;
        } else {
          // percentage-based (pediatric): compare raw doses, not HCe
          const currentDose = parseFloat(dosesRef.current[typeId]) || 0;
          const targetDose  = parseFloat(gcGoalRef.current[typeId]) || 0;
          const reduction = currentDose > 0 ? Math.round(((currentDose - targetDose) / currentDose) * 10000) / 10000 : 0;
          isError = reduction < (reductionData?.min ?? 0.10) || reduction > (reductionData?.max ?? 0.20);
        }
        newErrors[typeId] = isError;
      } else {
        newErrors[typeId] = false;
      }
    });
    setGoalErrors(newErrors);
  }, [state.population]);

  const validateAssessDate = useCallback((val) => {
    return validateDateWithinDayWindow(val, labCalRef, {
      referenceDate: state.formValues.labDrawDate,
      minDays: 28,
      maxDays: 84,
      warningOnly: true,
    });
  }, [state.formValues.labDrawDate]);

  // ── Inner-step navigation + final save ────────────────────────────────────
  const handleNextOrSave = useCallback(() => {
    if (innerStep < maxInnerStep) {
      setInnerStep((s) => s + 1);
    } else {
      const doseMap = Object.fromEntries(
        selectedTypes.map((id) => [id, dosesRef.current[id] ?? ''])
      );
      // Reduction goal is optional planning data. Normalize partially-typed
      // values (e.g. a lone ".") to '' so invalid fragments are never persisted.
      const isNumericString = (v) => /^(\d+\.?\d*|\.\d+)$/.test(v);
      const gcReductionGoal = Object.fromEntries(
        selectedTypes.map((id) => {
          const raw = gcGoalRef.current[id] ?? '';
          return [id, isNumericString(raw) ? raw : ''];
        })
      );
      const gcDownTitrationExperience = showDownTitrationExperience ? downTitrationExperience : '';
      dispatch({ type: 'SET_FIELD', field: 'gcSelectedTypes', value: selectedTypes });
      dispatch({ type: 'SET_FIELD', field: 'gcDoses',         value: doseMap });
      dispatch({ type: 'SET_FIELD', field: 'gcReductionGoal', value: gcReductionGoal });
      dispatch({
        type: 'SET_FIELD',
        field: 'gcDownTitrationExperience',
        value: gcDownTitrationExperience,
      });
      dispatch({ type: 'SET_FIELD', field: 'clinicalAssessmentDate', value: clinDateRef.current });
      dispatch({ type: 'SET_HCE_TOTAL', hceTotal: hceRef.current });
      onNext({
        hceTotal: hceRef.current,
        forceReadvance: true,
        formValues: {
          ...state.formValues,
          gcSelectedTypes: selectedTypes,
          gcDoses: doseMap,
          gcReductionGoal,
          gcDownTitrationExperience,
          clinicalAssessmentDate: clinDateRef.current,
        },
      });
    }
  }, [innerStep, maxInnerStep, dispatch, onNext, selectedTypes, downTitrationExperience, showDownTitrationExperience, state.formValues, state.population, validateAssessDate]);

  return (
    <GcStepShell
      title="GC Therapy &amp; Adjustments"
      showBack={false}
      onBack={onBack}
      onNext={handleNextOrSave}
      isNextDisabled={effectiveNextDisabled}
      isReview={isReview}
      nextLabel={isFinalInnerStep ? 'Save & continue' : 'Next'}
      nextVariant={isFinalInnerStep ? ['solid', 'solid--eggplant'] : ['solid', 'solid--orange']}
      nextIconType={isFinalInnerStep ? undefined : 'arrow-right'}
      stepNumber={stepNumber}
      innerStep={innerStep}
    >
      {/* ── Current GC therapy: multi-select types + per-type dose ─────── */}
      <fieldset className={styles.field_group}>
        <legend className={styles.field_label}>Current GC Therapy</legend>
        <p className={styles.sub_label}>Select all that apply and fill in accompanying dosages.</p>

        {GC_TYPES.map(({ id, label, placeholder, pediatricPlaceholder }) => (
          <div key={id} className={styles.gc_type_row}>
            <FormInputCheckbox
              name={`gc-type-${id}`}
              defaultChecked={savedTypes.includes(id)}
              onChange={() => handleTypeChange(id)}
              customClass={styles.checkbox}
            >
              {label}
            </FormInputCheckbox>

            {selectedTypes.includes(id) && (
              <div className={styles.dose_field}>
                <FormInputText
                  name={`gc-dose-${id}`}
                  label={`${label} dose in ${doseUnit}`}
                  placeholder={state.population === GC_CALC_POPULATION.PEDIATRIC ? pediatricPlaceholder : placeholder}
                  defaultValue={dosesRef.current[id] ?? ''}
                  inputMode="decimal"
                  customClass={styles.dose_input}
                  disableRequiredAsterisk
                  autoformatDecimalOnly
                  onChange={(e) => {
                    dosesRef.current[id] = e.target.value;
                    refresh(selectedTypes);
                    revalidateGoalErrors(selectedTypes);
                  }}
                />
                <span className={styles.dose_unit} aria-hidden="true">{doseUnit}</span>
              </div>
            )}
          </div>
        ))}
      </fieldset>

      {/* ── Shorter-acting GC callout — shown after fieldset at step 0 only ── */}
      {showSwitchCallout && innerStep === 0 && (
        <div className={styles.switch_callout}>
          <p className={styles.switch_callout_text}>
            Are you considering switching your patient to a shorter-acting GC?<Button
              type="tooltip"
              tooltipData={SWITCHING_GC_TOOLTIP}
              customClass={styles.tooltip_btn}
              aria-label="More information about switching to a shorter-acting GC"
            >
              &#9432;
            </Button>
          </p>
        </div>
      )}

      {/* ── Screen 1: GC dose reduction guidance (revealed after first Next) ── */}
      {innerStep >= 1 && (
        <div className={styles.guidance_section}>
          {/* Callout pinned to top of guidance so it stays visible after scroll */}
          {showSwitchCallout && (
            <div className={styles.switch_callout}>
              <p className={styles.switch_callout_text}>
                Are you considering switching your patient to a shorter-acting GC?<Button
                  type="tooltip"
                  tooltipData={SWITCHING_GC_TOOLTIP}
                  customClass={styles.tooltip_btn}
                  aria-label="More information about switching to a shorter-acting GC"
                >
                  &#9432;
                </Button>
              </p>
            </div>
          )}

          {/* HCe equivalents note — shown at innerStep >= 1 for non-HC only */}
          {showHceSection && (
            <>
              <p className={styles.hce_note}>
                This calculator is designed using hydrocortisone equivalents (HCe); the conversion
                factors for GC potency: {isPediatric ? '5' : '4'}&times; for prednisolone and prednisone,
                {' '}{isPediatric ? '6' : '5'}&times; for methylprednisolone, and ~60&times; for dexamethasone.
              </p>
            </>
          )}

          {/* Full dose reduction guidance — shown from step 2 onward */}
          {innerStep >= 1 && (
            <>
              {/* HCe computed dose — shown for all GC types when dose is entered */}
              {showHceSection && (
                <GcHceDisplay
                  hceTotal={Number(computedHce.toFixed(2))}
                  population={state.population}
                />
              )}

              {state.population === GC_CALC_POPULATION.PEDIATRIC && showHceSection && (
                <>
                  <p className={styles.hce_note_plain}>
                    Options for daily GC dose reductions are to reduce evening dose or simplify the regimen.
                  </p>
                  <p className={styles.hce_note}>
                    There are no clinical tests that can assess whether a given GC dose is adequate
                    for cortisol replacement (ie, physiologic) for an individual patient; clinical
                    expertise is needed. For most pediatric patients 8 to 11 mg/m&sup2;/day may be a
                    sufficient GC replacement dose; however, some patients may require higher doses
                    to prevent adrenal insufficiency. Symptoms of adrenal insufficiency overlap with
                    those for GC withdrawal (ie, when reducing from a higher to a lower
                    supraphysiologic dose) and may include headache, fatigue, abdominal pain,
                    and/or nausea.
                  </p>
                </>
              )}

              {showDownTitrationExperience && (
                <>
                  <div className={styles.experience_section}>
                    <h3 className={styles.experience_heading}>GC down-titration experience</h3>
                    <p className={styles.experience_question}>Have you down-titrated your patient&apos;s GC dose?</p>
                    <fieldset className={styles.experience_options}>
                      {GC_CALC_DOWN_TITRATION_EXPERIENCE.map(({ id, label }) => (
                        <FormInputCheckbox
                          key={id}
                          name={`gc-down-titration-${id}`}
                          typeRadio
                          radioGroupName="gc-down-titration-experience"
                          checked={downTitrationExperience === id}
                          onChange={() => setDownTitrationExperience(id)}
                        >
                          {label}
                        </FormInputCheckbox>
                      ))}
                    </fieldset>
                  </div>
                </>
              )}

              {state.population === GC_CALC_POPULATION.ADULT && (
              <p className={`${styles.hce_note} ${branch === 'high' ? '!mt-2' : ''}`.trim()}>
                There are no clinical tests that can assess whether a given GC dose is adequate
                for cortisol replacement (ie, physiologic) for an individual patient; clinical
                expertise is needed and should be individualized. GC dose should not be decreased
                below what is required for cortisol replacement in the individual patient. Mean
                daily cortisol production rate in adults is 10-15 mg/day HCe in healthy individuals.
                Symptoms of adrenal insufficiency overlap with those for GC withdrawal (ie, when
                reducing from a higher to a lower supraphysiologic dose) and may include headache,
                fatigue, abdominal pain, and/or nausea. Symptoms are more likely to be due to
                adrenal insufficiency if the GC dose is lower than 20 mg/day HCe.
              </p>)}

              <h3 className={styles.guidance_heading}>GC dose reduction strategy</h3>

              {state.population === GC_CALC_POPULATION.PEDIATRIC ? (
                <>
                  <p className={styles.guidance_body}>
                    The process for reducing GCs should be individualized and guided by your 
                    patient&apos;s complete clinical picture. After at least 4 weeks of CRENESSITY, 
                    assess A4 levels and use A4 levels to inform GC dose reduction.
                  </p>
                  <p className={styles.guidance_body}>
                    Daily GC dose can be reduced by 10% to 20% either by reducing the
                    evening dose or simplifying the regimen. For the simplest circadian
                    regimen, consider decreasing the least physiologic GC dose first. GC doses should not
                    be decreased below that required for cortisol replacement. Input the
                    reduced doses below.
                  </p>
                </>
              ) : (
                <>
                 <p className={styles.guidance_note}>
                    Assess A4 levels at least 4 weeks after initiating CRENESSITY to inform GC
                    dose reductions. Gradual reductions in GC doses may help to maintain androgen
                    control and reduce the risk of GC withdrawal symptoms.<Button
                      type="tooltip"
                      tooltipData={GC_REDUCTION_TOOLTIP}
                      customClass={styles.tooltip_btn}
                      aria-label="More information about GC reduction and monitoring"
                    >
                      &#9432;
                    </Button>
                  </p>

                  <p className={styles.guidance_primary}>
                    Based on current GC dose(s), consider reducing daily HCe dose by{' '}
                    <span className="text-nowrap">2.5–5 mg</span> every <span className="text-nowrap">~4-12 weeks*</span>
                  </p>

                 {/*  {branch === 'low' && (
                    <p className={styles.guidance_ai_note}>
                      Your patient may be at risk for adrenal insufficiency. Closely
                      monitor for symptoms of GC withdrawal when reducing dose.
                    </p>
                  )} */}

                  {/* <p className={styles.guidance_also}>The following guidance may also be considered:</p> */}
                  <ul className={styles.guidance_bullets}>
                    {branch === 'high' ? (
                      <li>
                        Review stress dosing plan with patients. The amount of cortisol
                        needed for stress dosing won&rsquo;t change as daily GC doses are lowered
                      </li>
                    ) : (
                      <>
                        <li>
                          For each decrease of ~10&nbsp;mg/day HCe, fludrocortisone dose
                          may need to be increased
                        </li>
                        <li>
                          Advise patients on monitoring for symptoms of GC withdrawal, adrenal
                          insufficiency, and mineralocorticoid insufficiency and discuss stress dosing
                        </li>
                        <li>
                          Since the mean physiologic GC dose is approximately 10-15 mg/day, withdrawal 
                          symptoms are more likely to be due to adrenal insufficiency if the GC dose is 
                          lower than 20 mg/day HCe
                        </li>
                      </>
                    )}
                  </ul>

                  <p className={styles.guidance_footnote}>
                    <span className="hang">*</span>The suggested timing between each step is flexible and can vary based on individual treatment needs and frequency of clinical touchpoints. The lower end of each range indicates the minimum recommended time to allow before proceeding.
                  </p>
                </>
              )}
            </>
          )}

          {/* ── GC reduction schedule — final inner step before save ─────── */}
          {isFinalInnerStep && (
            <>
              {state.population === GC_CALC_POPULATION.PEDIATRIC ? (
                <div className={styles.schedule_fields}>
                  <h4 className={styles.schedule_sub_heading}>Current GC reduction target</h4>
                  <p className="!mb-0 !text-hcp-charcoal">
                    The degree of GC dose reduction should be individualized based on the patient’s treatment goals and what is practical (eg, available tablet dose sizes) and should not be reduced below the dose needed for cortisol replacement. Further reductions may be required to reach maintenance GC dose.
                  </p>
                  <p className="!mb-0 !text-hcp-charcoal">
                    <strong>Enter the dose reduction for this taper step</strong>
                  </p>

                  {selectedTypes.map((id) => {
                    const gcType = GC_TYPES.find((t) => t.id === id);
                    if (!gcType) return null;
                    return (
                      <div key={id} className={styles.goal_row}>
                        <span className={styles.goal_label}>{gcType.label}</span>
                        <span className={styles.consider_label} aria-hidden="true">&nbsp;</span>
                        <div className={styles.dose_field}>
                          <FormInputText
                            name={`gc-goal-${id}`}
                            label={`${gcType.label} reduction goal in ${doseUnit}`}
                            placeholder={gcType.pediatricPlaceholder}
                            defaultValue={gcGoalRef.current[id] ?? ''}
                            inputMode="decimal"
                            customClass={styles.dose_input}
                            disableRequiredAsterisk
                            autoformatDecimalOnly
                            onChange={(e) => {
                              const val = e.target.value;
                              gcGoalRef.current[id] = val;
                              const targetDose  = parseFloat(val) || 0;
                              const currentDose = parseFloat(dosesRef.current[id]) || 0;
                              if (targetDose > 0 && currentDose > 0) {
                                const reduction = Math.round(((currentDose - targetDose) / currentDose) * 10000) / 10000;
                                const reductionData = gcType?.recommendedReduction?.pediatric;
                                const isError = reduction < (reductionData?.min ?? 0.10) || reduction > (reductionData?.max ?? 0.20);
                                setGoalErrors((prev) => ({ ...prev, [id]: isError }));
                              } else {
                                setGoalErrors((prev) => ({ ...prev, [id]: false }));
                              }
                            }}
                          />
                          <span className={styles.goal_unit} aria-hidden="true">{doseUnit}</span>
                        </div>
                        <span className={styles.current_dose_label}>Current dose:</span>
                        <span className={styles.consider_label} aria-hidden="true">&nbsp;</span>
                        <div className={styles.current_dose_display}>
                          <span className={styles.current_dose_value}>
                            {dosesRef.current[id] || '\u2014'}
                          </span>
                          <span className={`${styles.goal_unit} !text-hcp-eggplant`}>{doseUnit}</span>
                        </div>
                      </div>
                    );
                  })}

                  {Object.values(goalErrors).some(Boolean) && (
                    <p className={styles.goal_error}>
                      This GC reduction is outside the published expert guidance. Please assess based on your clinical judgement.
                    </p>
                  )}

                  <p className={styles.assess_timing_note}>
                    Assess treatment progress ~1&ndash;3 months (or sooner with clinical concern)
                    following dose reduction.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className={styles.guidance_heading}>GC reduction schedule</h3>

                  <p className={styles.schedule_desc}>
                    This section can help you plan towards a specific GC reduction goal.
                    You may use the guidance above to inform the desired value for reduction.
                  </p>

                  <div className={styles.schedule_fields}>
                    <h4 className={styles.schedule_sub_heading}>Current GC reduction target</h4>
                    <p className="!mb-0 !text-hcp-charcoal">
                      The degree of GC dose reduction should be individualized based on the patient’s treatment goals and what is practical (eg, available tablet dose sizes) and should not be reduced below the dose needed for cortisol replacement. Further reductions may be required to reach maintenance GC dose.
                    </p>
                    <p className="!mb-0 !text-hcp-charcoal">
                      <strong>Enter the dose reduction for this taper step</strong>
                    </p>
                    {selectedTypes.map((id) => {
                      const gcType = GC_TYPES.find((t) => t.id === id);
                      if (!gcType) return null;
                      const reductionData  = gcType.recommendedReduction?.[state.population];
                      const reductionRange = reductionData
                        ? (branch === 'high' ? reductionData.aboveHigh : reductionData.belowOrEqualHigh)
                        : null;
                      const reductionLabel = (reductionData?.unitType === 'mg' && reductionRange)
                        ? `${reductionRange.min}\u2013${reductionRange.max}\u00a0mg/day`
                        : '';
                      return (
                        <div key={id} className={styles.goal_row}>
                          <span className={styles.goal_label}>{gcType.label}</span>
                          <span><span className={styles.consider_label} aria-hidden={reductionData?.unitType !== 'mg'}>{reductionData?.unitType === 'mg' && reductionRange && <>Consider reducing by: {reductionLabel}</>}</span></span>
                          <div className={styles.dose_field}>
                            <FormInputText
                              name={`gc-goal-${id}`}
                              label={`${gcType.label} reduction goal in ${doseUnit}`}
                              placeholder={gcType.placeholder}
                              defaultValue={gcGoalRef.current[id] ?? ''}
                              inputMode="decimal"
                              customClass={styles.dose_input}
                              disableRequiredAsterisk
                              autoformatDecimalOnly
                              onChange={(e) => {
                                const val = e.target.value;
                                gcGoalRef.current[id] = val;
                                const targetRaw = parseFloat(val) || 0;
                                const currentRaw = parseFloat(dosesRef.current[id]) || 0;
                                if (targetRaw > 0 && currentRaw > 0) {
                                  let isError;
                                  if (reductionData?.unitType === 'mg' && reductionRange) {
                                    const reductionAmountRaw = currentRaw - targetRaw;
                                    isError = reductionAmountRaw < reductionRange.min || reductionAmountRaw > reductionRange.max;
                                  } else {
                                    const reduction = (currentRaw - targetRaw) / currentRaw;
                                    isError = reduction < (reductionData?.min ?? 0.10) || reduction > (reductionData?.max ?? 0.20);
                                  }
                                  setGoalErrors((prev) => ({ ...prev, [id]: isError }));
                                } else {
                                  setGoalErrors((prev) => ({ ...prev, [id]: false }));
                                }
                              }}
                              maxLength={5}
                            />
                            <span className={styles.goal_unit} aria-hidden="true">{doseUnit}</span>
                          </div>
                          <span className={styles.current_dose_label}>Current dose:</span>
                          <span className={styles.consider_label} aria-hidden="true">&nbsp;</span>
                          <div className={styles.current_dose_display}>
                            <span className={styles.current_dose_value}>
                              {dosesRef.current[id] || '\u2014'}
                            </span>
                            <span className={`${styles.goal_unit} !text-hcp-eggplant`}>{doseUnit}</span>
                          </div>
                        </div>
                      );
                    })}

                    {Object.values(goalErrors).some(Boolean) && (
                      <p className={styles.goal_error}>
                        This GC reduction is outside the published expert guidance. Please assess based on your clinical judgement.
                      </p>
                    )}

                    <div className={styles.date_field_group}>
                      <FormInputCalendar
                        ref={labCalRef}
                        name="lab-draw-clinical-assessment-date"
                        label={<>Lab draw/clinical assessment date<sup>&dagger;</sup></>}
                        defaultValue={clinDateRef.current}
                        // defaultValue={clinDateRef.current || labDateRef.current}
                        yearDropdownYearsBack={1}
                        yearDropdownYearsAhead={1}
                        disableDates="past"
                        errorMessage="This date is outside the timeframe from the published expert guidance (~4-12 weeks from most recent lab draw date)."
                        onChange={(e) => {
                          clinDateRef.current = e.target.value;
                          setIsClinDateFilled(Boolean(e.target.value));
                          validateAssessDate(e.target.value);
                        }}
                        onBlur={(e) => {
                          clinDateRef.current = e.target.value;
                          setIsClinDateFilled(Boolean(e.target.value));
                          validateAssessDate(e.target.value);
                        }}
                        customClass={styles.date_input}
                      />
                    </div>

                    <p className={styles.guidance_footnote}>
                      <sup className="hang not-italic text-base">&dagger;</sup>May include clinical and laboratory assessments such as symptoms,
                      blood pressure, ACTH, 17-OHP, A4, testosterone, renin, and electrolytes.
                    </p>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </GcStepShell>
  );
}

export default GcStepGcTherapyAdjustments;
